import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'react-aria-components/Button';
import { useSession } from '../../features/session/SessionContext';
import { bugReportErrorText, bugReportGateway, type BugReportInput } from '../../features/bug-reports/bug-report-gateway';
import { AccessibleDialog } from '../accessibility/AccessibleDialog';

const IMPACT_OPTIONS: Array<{ value: BugReportInput['impact']; label: string }> = [
  { value: 'minor', label: 'Fue un detalle, pude seguir sin problema' },
  { value: 'partial', label: 'Me costó, pero pude seguir' },
  { value: 'blocking', label: 'No pude continuar' },
];

const MIN_DETAILS_LENGTH = 10;

/**
 * Botón del pie de página para reportar un error de esta página en particular.
 *
 * Solo para cuentas identificadas: la cuenta y la URL donde se abrió el
 * modal se toman solas (sesión y `window.location.href`), así que quien
 * reporta no tiene que completar nada de eso — usa el mismo sistema que
 * `/panel/report-bug`, solo que con menos fricción.
 */
export function QuickBugReportButton() {
  const { state } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [details, setDetails] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [impact, setImpact] = useState<BugReportInput['impact']>('partial');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      bugReportGateway.create({
        title: details.trim().slice(0, 80),
        url: window.location.href,
        details: details.trim(),
        suggestion: suggestion.trim() || undefined,
        impact,
      }),
    onSuccess: () => {
      setError('');
      setDone(true);
    },
    onError: (failure) => setError(bugReportErrorText(failure)),
  });

  if (state.status !== 'authenticated') return null;

  const openModal = () => {
    setDetails('');
    setSuggestion('');
    setImpact('partial');
    setError('');
    setDone(false);
    setIsOpen(true);
  };

  const submitReport = () => {
    if (details.trim().length < MIN_DETAILS_LENGTH) {
      setError('Contanos un poco más sobre qué pasó, con unas palabras alcanza.');
      return;
    }
    submit.mutate();
  };

  return (
    <>
      <button type="button" className="btn btn-link footer-report-link p-0" onClick={openModal}>
        Reportá un error en esta página
      </button>
      <AccessibleDialog isOpen={isOpen} onOpenChange={setIsOpen} title="Reportar un error">
        {done ? (
          <>
            <p role="status">¡Gracias! Tu reporte quedó registrado. El equipo lo revisa y te avisa cuando quede resuelto.</p>
            <div className="accessible-dialog-actions">
              <Button className="btn btn-accent" type="button" onPress={() => setIsOpen(false)}>Cerrar</Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label" htmlFor="quick-bug-details">¿Cuál es el error? Contanos qué pasó.</label>
              <textarea
                className="form-control"
                id="quick-bug-details"
                rows={4}
                maxLength={5000}
                value={details}
                onChange={(event) => setDetails(event.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="quick-bug-suggestion">¿Cómo te gustaría que funcione? (opcional)</label>
              <textarea
                className="form-control"
                id="quick-bug-suggestion"
                rows={2}
                maxLength={2000}
                value={suggestion}
                onChange={(event) => setSuggestion(event.target.value)}
              />
            </div>
            <fieldset className="mb-3">
              <legend className="form-label">¿Qué tanto te trabó?</legend>
              {IMPACT_OPTIONS.map((option) => (
                <div className="form-check" key={option.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="quick-bug-impact"
                    id={`quick-bug-impact-${option.value}`}
                    checked={impact === option.value}
                    onChange={() => setImpact(option.value)}
                  />
                  <label className="form-check-label" htmlFor={`quick-bug-impact-${option.value}`}>{option.label}</label>
                </div>
              ))}
            </fieldset>
            {error ? <p className="error-message" role="alert">{error}</p> : null}
            <div className="accessible-dialog-actions">
              <Button className="btn btn-outline-primary" type="button" onPress={() => setIsOpen(false)}>Cancelar</Button>
              <Button className="btn btn-accent" type="button" isPending={submit.isPending} onPress={submitReport}>Enviar reporte</Button>
            </div>
          </>
        )}
      </AccessibleDialog>
    </>
  );
}
